
import { Box, Flex, Text } from "rebass/styled-components"
import { IGetEncounterSummaryOutputObject, IGetSoapDraftOutputObject } from "../../../../graphql/types/queries/appointment"
import { Fragment } from "react";
import { AiIcon } from "../../HpisWrapper";
import { CopyIcon, safariExpression } from "./ViewHpi";
import { toast } from "react-toastify";
import { AutoResizeTextarea } from "../../../../components/TextArea";
import theme from "../../../../styles/theme";

function NewlineText({ text }: { text: string }) {
  return (
    <>
      {text.split('\n').map((line, index) => (
        <Fragment key={index}>
          {line}
          <br />
        </Fragment>
      ))}
    </>
  );
}
const sectionTextStyle = {
  paddingLeft: "12px",
  borderLeft: "1px solid #344F7A99",
};

const Section = ({
  label,
  content,
  isList = false,
  fallback = "N/A",
}: {
  label: string;
  content?: string | string[];
  isList?: boolean;
  fallback?: string;
}) => (
  <>
    <Text fontSize="14px" fontWeight={500} mt={3}>
      {label}
    </Text>
    {isList && Array.isArray(content) ? (
      content.length > 0 ? (
        content.map((item) => (
          <Text
            key={item}
            paddingY="8px"
            ml={["12px"]}
            sx={sectionTextStyle}
            fontWeight={300}
            fontSize="14px"
          >
            {item}
          </Text>
        ))
      ) : (
        <Text
          paddingY="8px"
          ml={["12px"]}
          sx={{ ...sectionTextStyle, opacity: 0.75 }}
          fontWeight={300}
          fontSize="14px"
        >
          No ICDs reported
        </Text>
      )
    ) : (
      <Text
        paddingY="8px"
        ml={["12px"]}
        sx={sectionTextStyle}
        fontWeight={300}
        fontSize="14px"
      >
        <NewlineText text={(content as string) || fallback} />
      </Text>
    )}
  </>
);

export const AppointmentSummary = ({ data }: { data: IGetEncounterSummaryOutputObject }) => {
  if (!data) return null

  return (
    <Flex
      sx={{ gap: "12px", overflowY: "auto" }}
      flexDirection="column"
    >
      {data.error && (
        <Text
          fontSize="14px"
          fontWeight={500}
          mt={3}
          textAlign="center"
          color="red"
        >
          {data.error}
        </Text>
      )}

      <Section label="Patient" content={data.Patient_name} />
      <Section label="ICDs:" content={data.ICD10} isList />
      <Section label="Assessment" content={data.ASSESSMENT} />
      <Section label="Objective" content={data.OBJECTIVE} />
      <Section label="Subjective" content={data.SUBJECTIVE} />
      <Section label="Prescription Written" content={data.PRESCRIPTION_WRITTEN} />
    </Flex>
  );
}

export const SoapDraft = ({ data, handleBeforeResize, setData, handleAfterResize, resizeTrigger }: {
  data: IGetSoapDraftOutputObject, handleBeforeResize: () => void,
  resizeTrigger?: number,
  setData: (data: IGetSoapDraftOutputObject) => void,
  handleAfterResize: () => void
}) => {
  if (!data) return null

  return (
    <Flex
      sx={{ gap: "12px", overflowY: "auto" }}
      flexDirection="column"
    >
      <Flex mb={4} style={{ position: 'relative' }}>
        <Box style={{ width: '2px', background: '#B8D6F5', height: '100%' }} mr={3}></Box>
        <Flex flexDirection={'column'} flex={1}>
          <Flex justifyContent={'start'} alignItems={'center'} style={{ gap: '16px' }}>
            <Text fontSize={'16px'} fontWeight={600} mb={2}>Subjective</Text>
            <AiIcon />
            <Text fontWeight={300} fontSize={'12px'}>(editable)</Text>
            {safariExpression.test(navigator.userAgent) && (
              <Flex ml={3} justifyContent={'center'} style={{ cursor: 'pointer' }} onClick={() => {
                navigator.clipboard.writeText(data.subjective || '')
                toast.success("(SOAP) Subjective copied to clipboard", {
                  position: toast.POSITION.TOP_RIGHT,
                  autoClose: 5000
                });
              }}>
                <CopyIcon />
                <Text ml={2} fontWeight={600} fontSize={'12px'} style={{ color: '#0095F9', textDecoration: 'underline' }}>Copy</Text>
              </Flex>
            )}
          </Flex>
          <AutoResizeTextarea
            style={{ backgroundColor: 'white', border: 0, color: theme.color.primary, padding: 0, minHeight: '50px', fontSize: '14px' }}
            onChange={(e: any) => {
              handleBeforeResize();
              setData({
                ...data,
                subjective: e.target.value,
              })
            }}
            onHeightChange={handleAfterResize}
            resizeTrigger={resizeTrigger}
            value={data.subjective || ''} />
        </Flex>
      </Flex>

      <Flex mb={4} style={{ position: 'relative' }}>
        <Box style={{ width: '2px', background: '#B8D6F5', height: '100%' }} mr={3}></Box>
        <Flex flexDirection={'column'} flex={1}>
          <Flex justifyContent={'start'} alignItems={'center'} style={{ gap: '16px' }}>
            <Text fontSize={'16px'} fontWeight={600} mb={2}>Objective</Text>
            <AiIcon />
            <Text fontWeight={300} fontSize={'12px'}>(editable)</Text>
            {safariExpression.test(navigator.userAgent) && (
              <Flex ml={3} justifyContent={'center'} style={{ cursor: 'pointer' }} onClick={() => {
                navigator.clipboard.writeText(data.objective || '')
                toast.success("(SOAP) Objective copied to clipboard", {
                  position: toast.POSITION.TOP_RIGHT,
                  autoClose: 5000
                });
              }}>
                <CopyIcon />
                <Text ml={2} fontWeight={600} fontSize={'12px'} style={{ color: '#0095F9', textDecoration: 'underline' }}>Copy</Text>
              </Flex>
            )}
          </Flex>
          <AutoResizeTextarea
            style={{ backgroundColor: 'white', border: 0, color: theme.color.primary, padding: 0, minHeight: '50px', fontSize: '14px' }}
            onChange={(e: any) => {
              handleBeforeResize();
              setData({
                ...data,
                objective: e.target.value,
              })
            }}
            onHeightChange={handleAfterResize}
            resizeTrigger={resizeTrigger}
            value={data.objective || ''} />
        </Flex>
      </Flex>

      <Flex mb={4} style={{ position: 'relative' }}>
        <Box style={{ width: '2px', background: '#B8D6F5', height: '100%' }} mr={3}></Box>
        <Flex flexDirection={'column'} flex={1}>
          <Flex justifyContent={'start'} alignItems={'center'} style={{ gap: '16px' }}>
            <Text fontSize={'16px'} fontWeight={600} mb={2}>Assesment</Text>
            <AiIcon />
            <Text fontWeight={300} fontSize={'12px'}>(editable)</Text>
            {safariExpression.test(navigator.userAgent) && (
              <Flex ml={3} justifyContent={'center'} style={{ cursor: 'pointer' }} onClick={() => {
                navigator.clipboard.writeText(data.assessment || '')
                toast.success("(SOAP) Assesment copied to clipboard", {
                  position: toast.POSITION.TOP_RIGHT,
                  autoClose: 5000
                });
              }}>
                <CopyIcon />
                <Text ml={2} fontWeight={600} fontSize={'12px'} style={{ color: '#0095F9', textDecoration: 'underline' }}>Copy</Text>
              </Flex>
            )}
          </Flex>
          <AutoResizeTextarea
            style={{ backgroundColor: 'white', border: 0, color: theme.color.primary, padding: 0, minHeight: '50px', fontSize: '14px' }}
            onChange={(e: any) => {
              handleBeforeResize();
              setData({
                ...data,
                assessment: e.target.value,
              })
            }}
            onHeightChange={handleAfterResize}
            resizeTrigger={resizeTrigger}
            value={data.assessment || ''} />
        </Flex>
      </Flex>

      <Flex mb={4} style={{ position: 'relative' }}>
        <Box style={{ width: '2px', background: '#B8D6F5', height: '100%' }} mr={3}></Box>
        <Flex flexDirection={'column'} flex={1}>
          <Flex justifyContent={'start'} alignItems={'center'} style={{ gap: '16px' }}>
            <Text fontSize={'16px'} fontWeight={600} mb={2}>Plan</Text>
            <AiIcon />
            <Text fontWeight={300} fontSize={'12px'}>(editable)</Text>
            {safariExpression.test(navigator.userAgent) && (
              <Flex ml={3} justifyContent={'center'} style={{ cursor: 'pointer' }} onClick={() => {
                navigator.clipboard.writeText(data.plan || '')
                toast.success("(SOAP) Plan copied to clipboard", {
                  position: toast.POSITION.TOP_RIGHT,
                  autoClose: 5000
                });
              }}>
                <CopyIcon />
                <Text ml={2} fontWeight={600} fontSize={'12px'} style={{ color: '#0095F9', textDecoration: 'underline' }}>Copy</Text>
              </Flex>
            )}
          </Flex>
          <AutoResizeTextarea
            style={{ backgroundColor: 'white', border: 0, color: theme.color.primary, padding: 0, minHeight: '50px', fontSize: '14px' }}
            onChange={(e: any) => {
              handleBeforeResize();
              setData({
                ...data,
                plan: e.target.value,
              })
            }}
            onHeightChange={handleAfterResize}
            resizeTrigger={resizeTrigger}
            value={data.plan || ''} />
        </Flex>
      </Flex>
    </Flex>
  );
}